/**
 * Direct Authorize.Net Integration (No Edge Functions Required)
 * This bypasses Supabase Edge Functions and calls Authorize.Net directly
 */
export class AuthorizeNetDirect {
  constructor() {
    // PRODUCTION MODE - REAL TRANSACTIONS
    this.apiLoginId = import.meta.env.VITE_AUTHORIZE_NET_API_LOGIN_ID || '7Q7X8sn5Y';
    this.transactionKey = import.meta.env.VITE_AUTHORIZE_NET_TRANSACTION_KEY || '7M7ydt9T4SqL87T6';
    this.sandboxMode = false; // PRODUCTION MODE
    
    // Check if transaction key is missing
    if (!this.transactionKey) {
      console.error('❌ MISSING AUTHORIZE.NET TRANSACTION KEY!');
      console.error('Add to .env file: VITE_AUTHORIZE_NET_TRANSACTION_KEY=your_key_here');
      console.error('Get it from: https://account.authorize.net → Account → Settings → API Credentials & Keys');
    }
    
    // Use sandbox or production endpoint
    this.endpoint = this.sandboxMode 
      ? 'https://apitest.authorize.net/xml/v1/request.api'
      : 'https://api.authorize.net/xml/v1/request.api';
  }
  /**
   * Process credit card payment directly with Authorize.Net
   */
  async processPayment({ amount, cardNumber, expirationDate, cvv, customer = {} }) {
    try {
      console.log('Processing Authorize.Net payment...');
      
      // Build the transaction request
      const transactionRequest = {
        createTransactionRequest: {
          merchantAuthentication: {
            name: this.apiLoginId,
            transactionKey: this.transactionKey
          },
          transactionRequest: {
            transactionType: 'authCaptureTransaction',
            amount: amount.toFixed(2),
            payment: {
              creditCard: {
                cardNumber: String(cardNumber).replace(/\s/g, ''),
                expirationDate: String(expirationDate),
                cardCode: String(cvv)
              }
            },
            billTo: {
              firstName: customer.firstName || customer.name?.split(' ')[0],
              lastName: customer.lastName || customer.name?.split(' ')[1] || '',
              address: customer.address,
              city: customer.city,
              state: customer.state,
              zip: customer.zip,
              country: 'USA'
            },
            customerIP: customer.ip || '127.0.0.1'
          }
        }
      };

      // Make the API call
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(transactionRequest)
      });

      const data = await response.json();
      
      console.log('Authorize.net Response:', data);
      
      // Check for success
      if (data.transactionResponse && data.transactionResponse.responseCode === '1') {
        return {
          success: true,
          transactionId: data.transactionResponse.transId,
          authCode: data.transactionResponse.authCode,
          avsResponse: data.transactionResponse.avsResultCode,
          message: 'Payment successful'
        };
      } else {
        const errors = data.transactionResponse?.errors || [];
        const errorMessage = errors[0]?.errorText || 'Transaction declined';
        
        return {
          success: false,
          error: errorMessage,
          errorCode: errors[0]?.errorCode
        };
      }
    } catch (error) {
      console.error('Authorize.Net direct payment error:', error);
      return {
        success: false,
        error: error.message || 'Payment processing failed'
      };
    }
  }

  /**
   * Process ACH/eCheck payment
   */
  async processACHPayment(paymentData) {
    const { amount, routingNumber, accountNumber, accountType, customer } = paymentData;
    
    const transactionRequest = {
      createTransactionRequest: {
        merchantAuthentication: {
          name: this.apiLoginId,
          transactionKey: this.transactionKey
        },
        transactionRequest: {
          transactionType: 'authCaptureTransaction',
          amount: amount.toFixed(2),
          payment: {
            bankAccount: {
              accountType: accountType || 'checking',
              routingNumber: routingNumber,
              accountNumber: accountNumber,
              nameOnAccount: customer.name
            }
          },
          billTo: {
            firstName: customer.firstName || customer.name?.split(' ')[0],
            lastName: customer.lastName || customer.name?.split(' ')[1] || '',
            address: customer.address || '123 Main St',
            city: customer.city || 'Paradise',
            state: customer.state || 'TX',
            zip: customer.zip || '76073'
          }
        }
      }
    };

    const response = await fetch(this.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(transactionRequest)
    });

    return response.json();
  }

  /**
   * Validate credit card number (Luhn algorithm)
   */
  validateCardNumber(cardNumber) {
    const digits = cardNumber.replace(/\D/g, '');
    let sum = 0;
    let isEven = false;
    
    for (let i = digits.length - 1; i >= 0; i--) {
      let digit = parseInt(digits[i], 10);
      
      if (isEven) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }
      
      sum += digit;
      isEven = !isEven;
    }
    
    return sum % 10 === 0;
  }

  /**
   * Get card type from number
   */
  getCardType(cardNumber) {
    const patterns = {
      visa: /^4[0-9]{12}(?:[0-9]{3})?$/,
      mastercard: /^5[1-5][0-9]{14}$/,
      amex: /^3[47][0-9]{13}$/,
      discover: /^6(?:011|5[0-9]{2})[0-9]{12}$/
    };
    
    const cleaned = cardNumber.replace(/\D/g, '');
    
    for (const [type, pattern] of Object.entries(patterns)) {
      if (pattern.test(cleaned)) {
        return type;
      }
    }
    
    return 'unknown';
  }
}

const authorizeNetDirect = new AuthorizeNetDirect();
export { authorizeNetDirect };
export default authorizeNetDirect;
